import type { ReactNode } from "react";
import { Dialog as RadixDialog } from "radix-ui";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";
import { IconButton } from "@/components/ui/IconButton";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Buttons for the footer. The primary action goes last. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" };

/**
 * A modal.
 *
 * A panel that has been lifted off the page: the same white surface and the
 * same card radius, a wider shadow, and nothing else. No rule under the title
 * and none above the footer — space separates the three parts, which is what
 * keeps a confirmation from reading like a form.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        {/* The page behind a dialog dims and stays sharp:
            what is being asked about is usually about what is behind it. */}
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim/45" />
        <RadixDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2",
            "rounded-lg bg-surface-raised p-5 shadow-pop ring-1 ring-line sm:p-6",
            "max-h-[calc(100dvh-2rem)] overflow-y-auto",
            SIZES[size],
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-lg font-semibold text-fg">
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1.5 text-xs text-fg-subtle">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton tipless label="Close" icon={<X className="size-4" />} className="-me-2 -mt-1.5" />
            </RadixDialog.Close>
          </div>

          <div className="mt-5">{children}</div>

          {footer ? (
            <div className="mt-6 flex flex-wrap items-center justify-end gap-2">{footer}</div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
